import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Search, AlertTriangle, Ticket as TicketIcon, Users, Clock, CheckCircle2, Ban, PauseCircle, ChevronDown, ChevronRight, List, LayoutList } from 'lucide-react';
import TicketNewModal from './TicketNewModal';

const STATUS_META = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800', icon: TicketIcon },
  in_progress: { label: 'In progress', color: 'bg-amber-100 text-amber-800', icon: Clock },
  blocked: { label: 'Blocked', color: 'bg-orange-100 text-orange-800', icon: PauseCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: Ban },
};

const PRIORITY_META = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-700' },
  medium: { label: 'Medium', color: 'bg-slate-100 text-slate-800' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' },
};

const formatDate = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_) {
    return v;
  }
};

export default function TicketsList() {
  const navigate = useNavigate();
  const user = getUser();
  const role = user?.role;
  const canCreate = role === 'admin' || role === 'staff' || role === 'field_team';
  const isAdmin = role === 'admin';

  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Scope tab: all / assigned-to-me / created-by-me. Admin defaults to
  // "all"; staff & field_team default to "assigned-to-me" since that's
  // usually what they care about most.
  const [scope, setScope] = useState(isAdmin ? 'all' : 'assigned_to_me');
  // View mode: 'flat' (chronological list) or 'grouped' (collapsible
  // sections by category - Quality / Invoicing / General / etc). Default
  // 'grouped' because that's how users triage the backlog once there are
  // more than a few tickets. Persisted via localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('tickets_view_mode') || 'grouped';
    } catch (_) {
      return 'grouped';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('tickets_view_mode', viewMode); } catch (_) { /* no-op */ }
  }, [viewMode]);
  // Which category sections are expanded in grouped view. Collapsed by
  // default so the user sees one row per category and expands what they
  // want to work on.
  const [expandedCats, setExpandedCats] = useState({});

  const load = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (overdueOnly) params.overdue_only = true;
      if (isAdmin && assigneeFilter !== 'all') params.assigned_to = assigneeFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/api/tickets', { params });
      setTickets(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      // Uses the tickets-scoped assignable-users endpoint so field team
      // users (who cannot access /api/users) can also load the list.
      const { data } = await api.get('/api/tickets/assignable-users');
      const arr = Array.isArray(data) ? data : [];
      setUsers(arr);
    } catch (_) {
      // silent - filter dropdown is optional
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, overdueOnly, assigneeFilter]);

  const visibleTickets = useMemo(() => {
    const uid = user?.id;
    if (!uid) return tickets;
    if (scope === 'assigned_to_me') {
      return tickets.filter((t) => (t.assigned_to || []).includes(uid));
    }
    if (scope === 'created_by_me') {
      return tickets.filter((t) => t.assigned_by === uid);
    }
    return tickets;
  }, [tickets, scope, user?.id]);

  const stats = useMemo(() => {
    const s = { total: visibleTickets.length, open: 0, in_progress: 0, blocked: 0, completed: 0, overdue: 0 };
    visibleTickets.forEach((t) => {
      if (s[t.status] !== undefined) s[t.status] += 1;
      if (t.is_overdue) s.overdue += 1;
    });
    return s;
  }, [visibleTickets]);

  // Counts for the tab chips (independent of the current scope filter)
  const scopeCounts = useMemo(() => {
    const uid = user?.id;
    if (!uid) return { all: tickets.length, assigned_to_me: 0, created_by_me: 0 };
    return {
      all: tickets.length,
      assigned_to_me: tickets.filter((t) => (t.assigned_to || []).includes(uid)).length,
      created_by_me: tickets.filter((t) => t.assigned_by === uid).length,
    };
  }, [tickets, user?.id]);

  const nameById = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u.name || u.email || u.id));
    return m;
  }, [users]);

  // Group visibleTickets by category, with per-category stats, and sort
  // categories so the most actionable (overdue, then open) float to the top.
  const grouped = useMemo(() => {
    const by = {};
    visibleTickets.forEach((t) => {
      const cat = (t.category && String(t.category).trim()) || 'General';
      if (!by[cat]) by[cat] = { category: cat, tickets: [], open: 0, in_progress: 0, blocked: 0, completed: 0, overdue: 0 };
      const bucket = by[cat];
      bucket.tickets.push(t);
      if (bucket[t.status] !== undefined) bucket[t.status] += 1;
      if (t.is_overdue) bucket.overdue += 1;
    });
    const arr = Object.values(by);
    arr.sort((a, b) => {
      if (a.overdue !== b.overdue) return b.overdue - a.overdue;
      const aOpen = a.open + a.in_progress + a.blocked;
      const bOpen = b.open + b.in_progress + b.blocked;
      if (aOpen !== bOpen) return bOpen - aOpen;
      return a.category.localeCompare(b.category);
    });
    return arr;
  }, [visibleTickets]);

  const toggleCat = (cat) => setExpandedCats((prev) => ({ ...prev, [cat]: !prev[cat] }));
  const expandAllCats = () => setExpandedCats(Object.fromEntries(grouped.map((g) => [g.category, true])));
  const collapseAllCats = () => setExpandedCats({});

  // Row renderer shared between the flat view and each expanded category
  // section in the grouped view. Pulled out of the JSX so both branches
  // stay in lockstep (status/priority badges, "To me" / "By me" chips,
  // overdue indicator, click-to-navigate).
  const renderTicketRow = (t) => {
    const sm = STATUS_META[t.status] || STATUS_META.open;
    const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
    const uid = user?.id;
    const iAmAssignee = uid && (t.assigned_to || []).includes(uid);
    const iAmCreator = uid && t.assigned_by === uid;
    const rowAccent = iAmAssignee
      ? 'border-l-4 border-l-emerald-500 bg-emerald-50/30'
      : iAmCreator
      ? 'border-l-4 border-l-indigo-500'
      : '';
    return (
      <tr
        key={t.id}
        data-testid={`ticket-row-${t.ticket_no}`}
        className={`hover:bg-gray-50 cursor-pointer ${rowAccent}`}
        onClick={() => navigate(`/tickets/${t.id}`)}
      >
        <td className="px-4 py-3 font-mono text-xs text-gray-700">{t.ticket_no}</td>
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900">{t.title}</div>
          <div className="text-xs text-gray-500">
            {t.category || 'General'} · created {formatDate(t.created_at)}
          </div>
        </td>
        <td className="px-4 py-3">
          <Badge className={sm.color + ' border-0'}>{sm.label}</Badge>
        </td>
        <td className="px-4 py-3">
          <Badge className={pm.color + ' border-0'}>{pm.label}</Badge>
        </td>
        <td className="px-4 py-3">
          {iAmAssignee && (
            <Badge className="bg-emerald-100 text-emerald-800 border-0" data-testid="chip-to-me">
              To me
            </Badge>
          )}
          {iAmCreator && !iAmAssignee && (
            <Badge className="bg-indigo-100 text-indigo-800 border-0" data-testid="chip-by-me">
              By me
            </Badge>
          )}
          {iAmAssignee && iAmCreator && (
            <span className="ml-1 text-[10px] text-gray-500 uppercase">self</span>
          )}
        </td>
        <td className="px-4 py-3 text-xs text-gray-700">
          {(t.assigned_to_names && t.assigned_to_names.length > 0
            ? t.assigned_to_names
            : (t.assigned_to || []).map((aid) => nameById[aid] || aid)
          ).join(', ') || '—'}
        </td>
        <td className="px-4 py-3">
          <div className="text-xs">
            {formatDate(t.due_date)}
            {t.is_overdue && (
              <Badge className="ml-2 bg-red-100 text-red-700 border-0" data-testid="overdue-badge">
                Overdue
              </Badge>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <Layout title={isAdmin ? 'All Tickets' : role === 'field_team' ? 'My Tasks' : 'Tickets'}>
      <div className="max-w-7xl mx-auto space-y-6" data-testid="tickets-list-page">
        {/* Scope tabs - all / assigned to me / created by me */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 pb-1">
          {[
            { key: 'all', label: 'All' },
            { key: 'assigned_to_me', label: 'Assigned to me' },
            { key: 'created_by_me', label: 'Created by me' },
          ].map((tab) => {
            const active = scope === tab.key;
            const count = scopeCounts[tab.key];
            return (
              <button
                key={tab.key}
                onClick={() => setScope(tab.key)}
                data-testid={`tab-${tab.key}`}
                className={
                  'px-3 py-1.5 text-sm font-medium rounded-t-md border-b-2 -mb-[1px] flex items-center gap-1.5 ' +
                  (active
                    ? 'border-emerald-700 text-emerald-800'
                    : 'border-transparent text-gray-600 hover:text-gray-900')
                }
              >
                {tab.label}
                <span
                  className={
                    'inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 rounded-full text-[10px] ' +
                    (active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600')
                  }
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total" value={stats.total} testId="stat-total" />
          <StatCard label="Open" value={stats.open} testId="stat-open" />
          <StatCard label="In progress" value={stats.in_progress} testId="stat-in-progress" />
          <StatCard label="Blocked" value={stats.blocked} testId="stat-blocked" />
          <StatCard
            label="Overdue"
            value={stats.overdue}
            highlight={stats.overdue > 0}
            testId="stat-overdue"
          />
          <StatCard label="Completed" value={stats.completed} testId="stat-completed" />
        </div>

        {/* Filters + actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  data-testid="tickets-search"
                  placeholder="Search by title or ticket #"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && load()}
                  className="pl-9"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                  <SelectTrigger className="w-full md:w-52" data-testid="filter-assignee">
                    <SelectValue placeholder="Assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All assignees</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                variant={overdueOnly ? 'default' : 'outline'}
                onClick={() => setOverdueOnly((v) => !v)}
                data-testid="filter-overdue"
                className="whitespace-nowrap"
              >
                <AlertTriangle size={14} className="mr-1" /> Overdue only
              </Button>
              {/* View-mode toggle: Flat (date-sorted list) vs By Category
                  (collapsible sections). Persisted so each user keeps the
                  view they prefer. */}
              <div className="inline-flex rounded-md border overflow-hidden">
                <button
                  type="button"
                  onClick={() => setViewMode('grouped')}
                  className={`px-2.5 py-1.5 text-xs font-medium flex items-center gap-1 ${
                    viewMode === 'grouped' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="view-mode-grouped"
                  title="Group tickets by category"
                >
                  <LayoutList size={14} />
                  By Category
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('flat')}
                  className={`px-2.5 py-1.5 text-xs font-medium border-l flex items-center gap-1 ${
                    viewMode === 'flat' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="view-mode-flat"
                  title="Flat chronological list"
                >
                  <List size={14} />
                  Flat
                </button>
              </div>
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => navigate('/tickets/team')}
                  data-testid="team-dashboard-btn"
                  className="whitespace-nowrap"
                >
                  <Users size={14} className="mr-1" /> Team
                </Button>
              )}
              {canCreate && (
                <Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn">
                  <Plus size={14} className="mr-1" /> New Ticket
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ticket table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-gray-500">Loading tickets…</div>
            ) : visibleTickets.length === 0 ? (
              <div className="p-10 text-center text-gray-500" data-testid="tickets-empty">
                No tickets found.
              </div>
            ) : viewMode === 'grouped' ? (
              <div data-testid="tickets-grouped">
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-b bg-gray-50">
                  <span className="text-xs text-gray-500">
                    {grouped.length} categor{grouped.length === 1 ? 'y' : 'ies'} - sorted by overdue, then open count
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={expandAllCats}
                      className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 border rounded"
                      data-testid="tickets-expand-all"
                    >
                      Expand all
                    </button>
                    <button
                      type="button"
                      onClick={collapseAllCats}
                      className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 border rounded"
                      data-testid="tickets-collapse-all"
                    >
                      Collapse all
                    </button>
                  </div>
                </div>
                <div className="divide-y">
                  {grouped.map((g) => {
                    const isOpen = !!expandedCats[g.category];
                    const openish = g.open + g.in_progress + g.blocked;
                    return (
                      <div key={g.category} data-testid={`ticket-cat-${g.category.toLowerCase().replace(/\s+/g, '-')}`}>
                        <button
                          type="button"
                          onClick={() => toggleCat(g.category)}
                          className="w-full flex items-center justify-between gap-2 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                          data-testid={`ticket-cat-toggle-${g.category.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isOpen ? (
                              <ChevronDown size={16} className="text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
                            )}
                            <span className="font-semibold text-sm text-gray-800 truncate">{g.category}</span>
                            <span className="text-xs text-gray-500">({g.tickets.length})</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap justify-end">
                            {g.overdue > 0 && (
                              <Badge className="bg-red-100 text-red-700 border-0 text-[11px]">
                                <AlertTriangle size={10} className="mr-1" />
                                {g.overdue} overdue
                              </Badge>
                            )}
                            {openish > 0 && (
                              <Badge className="bg-blue-100 text-blue-800 border-0 text-[11px]">
                                {openish} open
                              </Badge>
                            )}
                            {g.completed > 0 && (
                              <Badge className="bg-green-100 text-green-800 border-0 text-[11px]">
                                {g.completed} done
                              </Badge>
                            )}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="overflow-x-auto bg-white">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600 border-y">
                                <tr>
                                  <th className="px-4 py-2">Ticket</th>
                                  <th className="px-4 py-2">Title</th>
                                  <th className="px-4 py-2">Status</th>
                                  <th className="px-4 py-2">Priority</th>
                                  <th className="px-4 py-2">Relation</th>
                                  <th className="px-4 py-2">Assignees</th>
                                  <th className="px-4 py-2">Due</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {g.tickets.map((t) => renderTicketRow(t))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase text-gray-600">
                    <tr>
                      <th className="px-4 py-3">Ticket</th>
                      <th className="px-4 py-3">Title</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Priority</th>
                      <th className="px-4 py-3">Relation</th>
                      <th className="px-4 py-3">Assignees</th>
                      <th className="px-4 py-3">Due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visibleTickets.map((t) => renderTicketRow(t))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {showNew && (
        <TicketNewModal
          users={users}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
    </Layout>
  );
}

function StatCard({ label, value, highlight, testId }) {
  return (
    <div
      className={
        'rounded-xl border px-4 py-3 ' + (highlight ? 'bg-red-50 border-red-200' : 'bg-white')
      }
      data-testid={testId}
    >
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div
        className={
          'text-2xl font-semibold ' + (highlight ? 'text-red-700' : 'text-gray-900')
        }
      >
        {value}
      </div>
    </div>
  );
}
